// src/components/SimpleComponents/SimpleTable.jsx
import React, { useMemo, useRef } from 'react';
import { Table } from 'antd';
import { ensureArray } from 'utils/helpers';

// Styles to remove border-bottom from last table row (both empty and with data)
const tableStyles = `
  .simple-table-wrapper .ant-table-tbody > tr:last-child > td {
    border-bottom: unset;
  }
`;

/**
 * Wraps Antd Table to guarantee:
 * - dataSource is always Array
 * - each row has a stable, unique key (rowKey fallback)
 * - duplicate keys are warned in console with context
 * Vertical wheel propagation is handled by CSS only (see _table.less).
 * @param {object} props
 * @returns {JSX.Element}
 */
const SimpleTable = ({ dataSource, rowKey = '_id', ...rest }) => {
  const safeData = useMemo(() => ensureArray(dataSource), [dataSource]);

  // Persist stable auto-keys per object identity
  const autoIdRef = useRef(0);
  const keyMapRef = useRef(new WeakMap());

  const getAutoKey = record => {
    if (typeof record !== 'object' || record === null) return `__auto_${++autoIdRef.current}`;
    const map = keyMapRef.current;
    if (map.has(record)) return map.get(record);
    const k = `__auto_${++autoIdRef.current}`;
    map.set(record, k);
    return k;
  };

  const rowKeyFn = useMemo(() => {
    if (typeof rowKey === 'function')
      return record => {
        const k = rowKey(record);
        return k ?? getAutoKey(record);
      };
    if (typeof rowKey === 'string')
      return record => record?.[rowKey] ?? record?.id ?? record?.key ?? getAutoKey(record);
    // fallback genérico
    return record => record?._id ?? record?.id ?? record?.key ?? getAutoKey(record);
  }, [rowKey]);

  // Debug: detect duplicates
  useMemo(() => {
    const seen = new Set();
    const dups = [];
    safeData.forEach((rec, i) => {
      const k = rowKeyFn(rec);
      if (seen.has(k)) dups.push({ index: i, key: k, rec });
      else seen.add(k);
    });
    if (dups.length) {
      // Mostra amostra para depurar rápido
      // eslint-disable-next-line no-console
      console.warn(
        '[SafeTable] Duplicate row keys detected:',
        dups.slice(0, 10).map(d => ({ index: d.index, key: d.key }))
      );
    }
  }, [safeData, rowKeyFn]);

  // Merge rootClassName with our empty state class
  const rootClassName = rest.rootClassName
    ? `simple-table-wrapper ${rest.rootClassName}`
    : 'simple-table-wrapper';

  return (
    <>
      <style>{tableStyles}</style>
      <Table
        {...rest}
        rootClassName={rootClassName}
        dataSource={safeData}
        rowKey={rowKeyFn}
        style={{ padding: 2, ...rest.style }}
      />
    </>
  );
};

export default SimpleTable;
